/**
 * Change detection for shadow maps. A map is re-rendered only when something that decides its
 * content changed (ShadowSystem keeps a key per light), so these hashes must see every change an
 * animation can make, and nothing else:
 * - a light keyframed by the animation system moves its entity (the fit / light transform are
 *   part of the key);
 * - a model that casts can move as a whole (its nodes' world transforms), play a GLB clip that
 *   moves its parts (each mesh instance's node), or deform (skinned bones, morph weights).
 * Values are rounded to 1e-4 so float noise in an idle scene never re-renders a map.
 */
import type * as pc from 'playcanvas';

const _hashBuf = new Float64Array(1);
const _hashInts = new Uint32Array(_hashBuf.buffer);

/** Fold a number (rounded to 1e-4) into a 32-bit hash. */
export function hashNumber(h: number, v: number): number {
    _hashBuf[0] = Math.round(v * 1e4) / 1e4;
    h = Math.imul(h ^ _hashInts[0], 0x9e3779b1) >>> 0;
    return Math.imul(h ^ _hashInts[1], 0x85ebca6b) >>> 0;
}

export const HASH_SEED = 0x811c9dc5;

/** The parts of a mesh instance the hash reads (pc.MeshInstance fits). */
export interface CasterLike {
    node: { getWorldTransform(): { data: ArrayLike<number> } };
    aabb: { center: { x: number; y: number; z: number }; halfExtents: { x: number; y: number; z: number } };
    skinInstance?: { bones?: ReadonlyArray<{ getWorldTransform(): { data: ArrayLike<number> } }> } | null;
    morphInstance?: { _weights?: ArrayLike<number> } | null;
}

/** Everything about these casters that changes what they put in a shadow map. */
export function meshCasterHash(casters: ReadonlyArray<CasterLike | pc.MeshInstance>, h = HASH_SEED): number {
    for (const c of casters as ReadonlyArray<CasterLike>) {
        const m = c.node.getWorldTransform().data;
        for (let i = 0; i < 16; i++) if (i % 4 !== 3) h = hashNumber(h, m[i]);
        const { center, halfExtents } = c.aabb;
        h = hashNumber(h, center.x);
        h = hashNumber(h, center.y);
        h = hashNumber(h, center.z);
        h = hashNumber(h, halfExtents.x);
        h = hashNumber(h, halfExtents.y);
        h = hashNumber(h, halfExtents.z);
        const bones = c.skinInstance?.bones;
        if (bones) {
            for (const bone of bones) {
                const b = bone.getWorldTransform().data;
                for (let i = 0; i < 16; i++) if (i % 4 !== 3) h = hashNumber(h, b[i]);
            }
        }
        const weights = c.morphInstance?._weights;
        if (weights) for (let i = 0; i < weights.length; i++) h = hashNumber(h, weights[i]);
    }
    return h;
}

/**
 * How dark a light's shadow on the scan is with relighting off (the shadow darkens the scan's
 * baked colour): the scene's strength, faded with the light while its intensity is animated
 * (or set) below what it was authored with, so a light that fades out takes its shadow with it,
 * as models' shadows do. Brighter than authored keeps the full strength.
 */
export function shadowStrengthForLight(strength: number, intensity: number, authoredIntensity: number | undefined): number {
    if (!(typeof authoredIntensity === 'number' && authoredIntensity > 0)) return strength;
    const k = Math.min(1, Math.max(0, intensity / authoredIntensity));
    return strength * k;
}
